
import { Helmet } from "react-helmet";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FeatureCard from "@/components/FeatureCard";
import { Button } from "@/components/ui/button";
import { MessageSquare } from "lucide-react";

const Servicos = () => {
  const services = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      ),
      title: "SEO Local",
      description: "Otimizamos sua presença digital para destacar seu negócio em buscas locais no Google e Google Maps.",
      link: "/servicos/seo-local"
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="20" height="14" x="2" y="3" rx="2"/>
          <line x1="8" x2="16" y1="21" y2="21"/>
          <line x1="12" x2="12" y1="17" y2="21"/>
        </svg>
      ),
      title: "Otimização de Sites",
      description: "Melhoramos sua presença online com sites rápidos e otimizados para mecanismos de busca e conversão.",
      link: "/servicos/otimizacao-sites"
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
          <path d="m2 12 3.5 1.5L7 17l4.5-1L12 22"/>
          <path d="M10 7.5 12.5 5l2 3L17.5 6.5 19 9"/>
        </svg>
      ),
      title: "Google Meu Negócio",
      description: "Gerenciamento completo do seu perfil no Google Meu Negócio para maximizar sua visibilidade local.",
      link: "/servicos/google-meu-negocio"
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 10c.7-1.74 2.27-3 4-3 1.86 0 3.4 1.43 4 3"/><path d="M10 9a1 1 0 0 0-2 0"/><path d="m19.5 12.572-.42.425a.995.995 0 0 1-1.429-.005l-2.08-2.134a.991.991 0 0 1 .028-1.399l.401-.401"/><path d="M7 20h6.5"/><path d="M13 15.055V16.5a2 2 0 0 1-2 2h-1a2 2 0 0 0-1.1.35l-2.2 1.65a2 2 0 0 1-2.4-.05L2.5 19c-.3-.2-.5-.5-.5-.9v-7.2c0-1.2 1.5-1.8 2.4-1l3.6 2.8"/><path d="M14 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6"/><path d="M17 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6"/><path d="M21 11.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3"/></svg>
      ),
      title: "Avaliações Online",
      description: "Estratégias para aumentar o número e a qualidade das avaliações online do seu negócio.",
      link: "/servicos/avaliacoes-online"
    }
  ];

  // Schema data
  const serviceFAQSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "O que é SEO Local?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SEO Local é um conjunto de estratégias para fazer sua empresa aparecer nas buscas do Google por região, como 'eletricista em Belo Horizonte', 'clínica odontológica em Campinas', etc. Isso traz mais ligações, visitas e orçamentos para seu negócio."
        }
      },
      {
        "@type": "Question",
        "name": "Como o SEO Local pode ajudar meu negócio?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "O SEO Local ajuda seu negócio a ser encontrado por clientes em sua área geográfica quando eles estão procurando pelos serviços ou produtos que você oferece. Isso aumenta a visibilidade da sua empresa no Google e no Google Maps, gerando mais ligações, visitas e conversões."
        }
      },
      {
        "@type": "Question",
        "name": "Quanto tempo leva para ver resultados com SEO Local?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Os resultados do SEO Local geralmente começam a aparecer entre 1 a 3 meses, dependendo da competitividade do seu mercado e da situação atual da sua presença online. Melhorias contínuas são observadas ao longo de 6 a 12 meses de trabalho consistente."
        }
      }
    ]
  };

  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "SEO Local",
    "provider": {
      "@type": "Organization",
      "name": "LocalUp",
      "url": "https://localup.com.br"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Brasil"
    },
    "description": "Serviços especializados em SEO Local para pequenas e médias empresas, aumentando a visibilidade nos resultados de busca local do Google e no Google Maps."
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Serviços de SEO Local para Empresas | LocalUp</title>
        <meta name="description" content="Conheça nossos serviços especializados em SEO Local, otimização de presença online, gestão de Google Meu Negócio e estratégias para avaliações positivas." />
        <meta name="keywords" content="SEO Local, otimização de sites, Google Meu Negócio, avaliações online, marketing digital local" />
        <link rel="canonical" href="https://localup.com.br/servicos" />
        <script type="application/ld+json">
          {JSON.stringify(serviceFAQSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(servicesSchema)}
        </script>
      </Helmet>
      
      {/* Hero da página de serviços */}
      <section className="py-16 px-6 md:px-12 bg-gradient-to-br from-white to-seo-light">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-6 text-seo-dark">Nossos Serviços de SEO Local</h1>
          <p className="text-xl mb-8 text-gray-700">
            Soluções completas para aumentar a visibilidade da sua empresa nas buscas locais e atrair mais clientes qualificados.
          </p>
        </div>
      </section>

      {/* Lista de serviços */}
      <section className="py-16 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:shadow-xl transition-all">
                <div className="mb-4 text-seo-primary text-3xl">{service.icon}</div>
                <h2 className="text-2xl font-semibold mb-3 text-seo-dark">{service.title}</h2>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <Button 
                  onClick={() => window.location.href = service.link}
                  className="bg-seo-primary hover:bg-seo-primary/90"
                >
                  Saiba mais
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-seo-light">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">Pronto para aumentar sua presença local?</h2>
          <p className="text-lg mb-8 text-gray-700">
            Entre em contato para uma análise gratuita da sua presença digital e descubra como podemos ajudar seu negócio.
          </p>
          <Button 
            onClick={() => window.open('https://wa.me/5511951974403', '_blank')}
            className="bg-green-500 hover:bg-green-600 text-white text-lg px-8 py-6 rounded-lg shadow-lg flex items-center mx-auto gap-2"
          >
            <MessageSquare className="w-5 h-5" /> Fale conosco no WhatsApp
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Servicos;
